/**
 * RLQ — front-end-only mock data for local dev / demo paths.
 *
 * Activated by `?lobbyMock=1` query param OR
 * `import.meta.env.VITE_FEATURE_LOBBY_MOCK === 'true'`. The provider is
 * deliberately deterministic so screenshots stay stable. Real production
 * traffic always hits `/v3/sessions`.
 */

import type { SessionDto } from '@retail-ops/shared-types';
import type { Origin } from '@retail-ops/shared-types/enums';

const MIN = 60_000;

interface MockSeed {
  customerName?: string;
  cifId?: string;
  serviceType: string;
  officerName?: string;
  officerCode?: string;
  branch: string;
  origin: Origin;
  agedMinutes: number;
}

const SEEDS: ReadonlyArray<MockSeed> = [
  {
    customerName: 'Margaret Allen',
    cifId: 'cif-410088',
    serviceType: 'cif-update',
    branch: '1',
    origin: 'in-branch',
    agedMinutes: 3,
  },
  {
    serviceType: 'new-account',
    branch: '1',
    origin: 'kiosk',
    officerCode: 'KIOSK',
    agedMinutes: 7,
  },
  {
    customerName: 'Daniel Park',
    serviceType: 'sign-docs',
    branch: '2',
    origin: 'in-branch',
    officerName: 'Lori H.',
    officerCode: 'LH04',
    agedMinutes: 12,
  },
  {
    customerName: 'Elena Vasquez',
    cifId: 'cif-908112',
    serviceType: 'ira-contribution',
    branch: '1',
    origin: 'online',
    agedMinutes: 18,
  },
  {
    customerName: 'Theo Becker',
    serviceType: 'sdb-service',
    branch: '3',
    origin: 'in-branch',
    agedMinutes: 27,
  },
  {
    serviceType: 'account-maintenance',
    branch: '1',
    origin: 'kiosk',
    officerCode: 'KIOSK',
    agedMinutes: 33,
  },
  {
    customerName: 'Renee Choudhury',
    serviceType: 'cd-action',
    branch: '4',
    origin: 'in-branch',
    officerName: 'Marcus B.',
    officerCode: 'MB12',
    agedMinutes: 6,
  },
  {
    customerName: 'Walter Pham',
    serviceType: 'other',
    branch: '1',
    origin: 'in-branch',
    agedMinutes: 14,
  },
];

function buildId(seed: MockSeed, index: number): string {
  // Deterministic, opaque id — never rendered, only used in click handlers.
  return `mock-${index.toString(16).padStart(4, '0')}-${seed.branch}-${seed.serviceType}`;
}

export function buildMockLobbySessions(now: Date = new Date()): ReadonlyArray<SessionDto> {
  const ts = now.getTime();
  return SEEDS.map((seed, i) => {
    const startedAt = new Date(ts - seed.agedMinutes * MIN).toISOString();
    const dto: SessionDto = {
      sessionId: buildId(seed, i),
      sessionType: 'lobby',
      serviceType: seed.serviceType,
      officer: seed.officerName ?? 'unassigned',
      officerCode: seed.officerCode ?? 'UNCLAIMED',
      branch: seed.branch,
      origin: seed.origin,
      status: 'active',
      startedAt: startedAt as unknown as SessionDto['startedAt'],
    };
    if (seed.cifId) (dto as { cifId?: string }).cifId = seed.cifId;
    if (seed.officerName) (dto as { officerName?: string }).officerName = seed.officerName;
    if (seed.customerName) {
      // Stash the visitor name on the DTO so `sessionToVisitor()` can use it.
      (dto as unknown as { customerName: string }).customerName = seed.customerName;
    }
    return dto;
  });
}

export function isLobbyMockEnabled(): boolean {
  if (typeof window !== 'undefined') {
    const search = window.location.search;
    if (search.includes('lobbyMock=1') || search.includes('lobbyMock=true')) return true;
  }
  // Vite env — string "true" only.
  const env = import.meta.env as Record<string, string | undefined>;
  return env['VITE_FEATURE_LOBBY_MOCK'] === 'true';
}
